import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as subcategoriesApi from "@api/subcategories.api";
import type { Subcategory } from "@/types";

interface SubcategoriesState {
  items: Subcategory[];
  loading: boolean;
}

const initialState: SubcategoriesState = {
  items: [],
  loading: false,
};

export const fetchSubcategories = createAsyncThunk(
  "subcategories/fetchAll",
  async (categoryId?: string) => {
    return await subcategoriesApi.getSubcategories(categoryId);
  },
);

export const createSubcategory = createAsyncThunk(
  "subcategories/create",
  async (data: { name: string; description?: string; categoryId: string }) => {
    return await subcategoriesApi.createSubcategory(data);
  },
);

export const updateSubcategory = createAsyncThunk(
  "subcategories/update",
  async ({
    id,
    data,
  }: {
    id: string;
    data: { name?: string; description?: string; categoryId?: string };
  }) => {
    return await subcategoriesApi.updateSubcategory(id, data);
  },
);

export const removeSubcategory = createAsyncThunk(
  "subcategories/remove",
  async (id: string) => {
    await subcategoriesApi.deleteSubcategory(id);
    return id;
  },
);

const subcategoriesSlice = createSlice({
  name: "subcategories",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubcategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSubcategories.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading = false;
      })
      .addCase(fetchSubcategories.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createSubcategory.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })
      .addCase(updateSubcategory.fulfilled, (state, action) => {
        const idx = state.items.findIndex((s) => s.id === action.payload.id);
        if (idx !== -1) state.items[idx] = action.payload;
      })
      .addCase(removeSubcategory.fulfilled, (state, action) => {
        state.items = state.items.filter((s) => s.id !== action.payload);
      });
  },
});

export default subcategoriesSlice.reducer;
